import React from 'react';
import { Calendar, MapPin, User, MessageSquare, CheckCircle2, ClipboardList } from 'lucide-react';
import { Task } from '../../services/tasksService';
import Badge from '../common/Badge';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import { statusVariant, severityBadgeCss } from '../../utils/statusVariant';

interface Props {
    tasks: Task[];
    loading?: boolean;
    emptyText?: string;
    onEdit?: (task: Task) => void;
    onDelete?: (task: Task) => void;
    /** Hide the "Source" column — useful when the list is already scoped to one source. */
    hideSource?: boolean;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try {
        return new Date(d).toLocaleDateString(undefined,
            { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return d; }
};

const CardSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="action-card action-card--skeleton">
                <div className="action-card__skel-header">
                    <Skeleton.Box width={34} height={34} radius={8} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Skeleton.Box width="60%" height={12} />
                        <Skeleton.Box width="40%" height={10} />
                    </div>
                    <Skeleton.Pill width={56} height={20} />
                </div>
                <div className="action-card__skel-meta">
                    <Skeleton.Box width={80} height={10} />
                    <Skeleton.Box width={100} height={10} />
                </div>
            </div>
        ))}
    </>
);

const TaskList: React.FC<Props> = ({
    tasks, loading, emptyText = 'No tasks.', onEdit, hideSource,
}) => {
    if (loading) {
        return (
            <div className="action-card-grid">
                <CardSkeleton count={6} />
            </div>
        );
    }

    if (!tasks.length) {
        return <NoDataFound title="No Tasks Found" message={emptyText} />;
    }

    const today = new Date(); today.setHours(0, 0, 0, 0);

    return (
        <div className="action-card-grid">
            {tasks.map(t => {
                const isOverdue = t.due_date && new Date(t.due_date) < today
                    && !['DONE', 'VERIFIED', 'REJECTED', 'CANCELLED'].includes(t.status);

                const links = t.linked_entities || [];
                const fallbackLink = !links.length && t.linked_type_code && t.linked_label
                    ? [{ id: -1, type_code: t.linked_type_code, label: t.linked_label, code: null }]
                    : [];
                const allLinks = [...links, ...fallbackLink];

                const sourceLinks = !hideSource && t.source_type_code
                    ? [{ type_code: `FROM ${t.source_type_code}`, label: t.source_label }]
                    : [];

                return (
                    <div
                        key={t.id}
                        className="action-card"
                        role={onEdit ? 'button' : undefined}
                        tabIndex={onEdit ? 0 : undefined}
                        onClick={() => onEdit && onEdit(t)}
                        onKeyDown={onEdit ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(t); }
                        } : undefined}
                        data-testid={`BTN_task_list_row_${t.id}_open`}
                        style={!onEdit ? { cursor: 'default' } : undefined}
                    >
                        {/* Header: icon + role/title/description + status badge */}
                        <div className="action-card__header">
                            <div className="action-card__icon">
                                <ClipboardList size={16} />
                            </div>
                            <div className="action-card__title-block">
                                {t.my_role && (
                                    <span className="action-card__subtitle" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'var(--font-weight-heading)' }}>
                                        {t.my_role.replace(/_/g, ' ')}
                                    </span>
                                )}
                                <span className="action-card__title">{t.name}</span>
                                {t.description && (
                                    <span className="action-card__subtitle">{t.description}</span>
                                )}
                            </div>
                            <div className="action-card__badge" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                                <Badge variant={statusVariant(t.status)} size="xs">
                                    {t.status.replace(/_/g, ' ')}
                                </Badge>
                                {t.priority && (
                                    <span className={severityBadgeCss(t.priority, 'xs')}>
                                        {t.priority}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Meta: due date, doer, source, linked entities */}
                        {(t.due_date || t.doer_name || sourceLinks.length > 0 || allLinks.length > 0 || t.feedback_text || t.effectiveness_check) && (
                            <div className="action-card__meta">
                                {t.due_date && (
                                    <span className={`action-card__meta-item${isOverdue ? ' action-card__meta-item--overdue' : ''}`}>
                                        <Calendar size={11} />
                                        {fmtDate(t.due_date)}
                                        {isOverdue && ' (overdue)'}
                                    </span>
                                )}
                                {t.doer_name && (
                                    <span className="action-card__meta-item">
                                        <User size={11} />
                                        {t.doer_name}
                                    </span>
                                )}
                                {sourceLinks.map((s, i) => (
                                    <span key={i} className="action-card__meta-item">
                                        <span className="status-badge xs neutral">{s.type_code}</span>
                                        {s.label}
                                    </span>
                                ))}
                                {allLinks.slice(0, 1).map((l, idx) => (
                                    <span key={l.id ?? idx} className="action-card__meta-item">
                                        <MapPin size={11} />
                                        {l.type_code && <span className="status-badge xs info">{l.type_code}</span>}
                                        {l.code && <span style={{ fontSize: 10 }}>{l.code}</span>}
                                        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.label}</span>
                                    </span>
                                ))}
                                {allLinks.length > 1 && (
                                    <span className="action-card__meta-item">+{allLinks.length - 1}</span>
                                )}
                                {t.feedback_text && (
                                    <span className="action-card__meta-item">
                                        <MessageSquare size={11} /> Feedback recorded
                                    </span>
                                )}
                                {t.effectiveness_check && (
                                    <span className="action-card__meta-item">
                                        <CheckCircle2 size={11} /> {t.effectiveness_check}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TaskList;
